import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import StatusBadge, { PlanBadge } from '../../../components/base/StatusBadge';
import { fetchOrderPlan, patchOrderPlanStatus, type ApproveOrderPlanPayload } from '../../../api/orderPlans.api';
import type { AdminOrderPlanDetail } from '../../../types/orderPlan.types';
import { formatPlanMoney } from '../../../lib/planCurrency';

interface OrderPlanDetailModalProps {
  orderPlanId: number | null;
  onClose: () => void;
  onUpdated: () => void;
  onShowCredentials?: (payload: ApproveOrderPlanPayload, fallbackEmail?: string) => void;
}

function isAwaiting(status?: string | null): boolean {
  return status === 'payment_submitted' || status === 'pending';
}

export default function OrderPlanDetailModal({
  orderPlanId,
  onClose,
  onUpdated,
  onShowCredentials,
}: OrderPlanDetailModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<AdminOrderPlanDetail | null>(null);
  const [confirmAction, setConfirmAction] = useState<'approved' | 'rejected' | null>(null);

  useEffect(() => {
    if (orderPlanId == null) {
      setDetail(null);
      setError('');
      setConfirmAction(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setConfirmAction(null);
    void fetchOrderPlan(orderPlanId).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (r.ok === false) {
        if (r.unauthorized) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(r.message);
        setDetail(null);
        return;
      }
      setDetail(r.orderPlan);
    });
    return () => {
      cancelled = true;
    };
  }, [orderPlanId, navigate]);

  if (orderPlanId == null) return null;

  const applyStatus = async (status: 'approved' | 'rejected') => {
    if (!detail) return;
    setSaving(true);
    setError('');
    const r = await patchOrderPlanStatus(detail.id, { status });
    setSaving(false);
    setConfirmAction(null);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      return;
    }
    if (status === 'approved' && r.data.admin) {
      onShowCredentials?.(r.data, detail.email);
    }
    onUpdated();
    onClose();
  };

  const awaiting = isAwaiting(detail?.status);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg my-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">{t('order_plans.detail.title')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-12 text-gray-500">
              <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin" />
              <p className="text-sm">{t('order_plans.loading')}</p>
            </div>
          ) : error ? (
            <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</p>
          ) : detail ? (
            <>
              <div className="rounded-xl bg-gray-50 ring-1 ring-gray-100 p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('order_plans.detail.customer')}
                  </p>
                  <StatusBadge status={detail.status} />
                </div>
                <p className="text-lg font-bold text-gray-900">{detail.name}</p>
                <div className="grid gap-2 text-sm text-gray-700">
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line text-gray-400" />
                    <span className="break-all">{detail.email}</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-phone-line text-gray-400" />
                    {detail.phone}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-400 font-medium">{t('order_plans.col.tenant_id')}</p>
                  <p className="text-gray-900 font-mono text-xs break-all">{detail.tenant_id || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">{t('order_plans.col.subscription_id')}</p>
                  <p className="text-gray-900 font-mono">{detail.subscription_id || '—'}</p>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t('order_plans.detail.plan_section')}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <PlanBadge plan={detail.plan?.title} />
                  <span className="text-sm text-gray-600">
                    {detail.plan?.price
                      ? formatPlanMoney(detail.plan.price, detail.plan.currency)
                      : '-'}{' '}
                    · {detail.plan?.days ?? '-'} {t('plans.duration_days')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  {detail.plan?.description ?? ''}
                </p>
              </div>

              {detail.payment_reference || detail.payment_proof_url ? (
                <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4 space-y-3">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                    {t('order_plans.detail.payment_section')}
                  </p>
                  {detail.payment_reference ? (
                    <p className="text-sm text-gray-800">
                      <span className="font-medium text-gray-500">
                        {t('order_plans.detail.payment_reference')}:
                      </span>{' '}
                      <span className="font-mono" dir="ltr">
                        {detail.payment_reference}
                      </span>
                    </p>
                  ) : null}
                  {detail.payment_submitted_at ? (
                    <p className="text-xs text-gray-500">
                      {t('order_plans.detail.payment_submitted_at')}: {detail.payment_submitted_at}
                    </p>
                  ) : null}
                  {detail.payment_proof_url ? (
                    <a
                      href={detail.payment_proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-800"
                    >
                      <i className="ri-image-line" />
                      {t('order_plans.detail.payment_proof')}
                    </a>
                  ) : null}
                  {detail.payment_proof_url ? (
                    <img
                      src={detail.payment_proof_url}
                      alt=""
                      className="max-h-56 rounded-xl border border-gray-200 object-contain bg-white"
                    />
                  ) : null}
                  <p className="text-xs text-amber-900 leading-relaxed">
                    {t('order_plans.detail.payment_hint')}
                  </p>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-400 font-medium">{t('order_plans.col.created_at')}</p>
                  <p className="text-gray-800">{detail.created_at}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">{t('order_plans.col.updated_at')}</p>
                  <p className="text-gray-800">{detail.updated_at}</p>
                </div>
              </div>

              {awaiting ? (
                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <p className="text-xs text-gray-400 font-medium">{t('order_plans.detail.status')}</p>
                  {confirmAction ? (
                    <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 p-4 space-y-3">
                      <p className="text-sm text-gray-800">
                        {confirmAction === 'approved'
                          ? t('order_plans.detail.confirm_approve')
                          : t('order_plans.detail.confirm_reject')}
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => setConfirmAction(null)}
                          className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-white cursor-pointer disabled:opacity-40"
                        >
                          {t('actions.cancel')}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void applyStatus(confirmAction)}
                          className={`flex-1 py-2.5 rounded-lg text-sm font-medium text-white cursor-pointer disabled:opacity-40 ${
                            confirmAction === 'approved'
                              ? 'bg-emerald-600 hover:bg-emerald-700'
                              : 'bg-rose-600 hover:bg-rose-700'
                          }`}
                        >
                          {saving
                            ? t('order_plans.detail.saving')
                            : confirmAction === 'approved'
                              ? t('order_plans.detail.approve')
                              : t('order_plans.detail.reject')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => setConfirmAction('approved')}
                        className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 cursor-pointer disabled:opacity-40"
                      >
                        <i className="ri-check-line me-1" />
                        {t('order_plans.detail.approve')}
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => setConfirmAction('rejected')}
                        className="flex-1 py-2.5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 cursor-pointer disabled:opacity-40"
                      >
                        <i className="ri-close-line me-1" />
                        {t('order_plans.detail.reject')}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="px-6 pb-5">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            {t('actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
